import { FC, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { IonRouterLink } from '@ionic/react'
import { Navigation, Pagination, Virtual } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { WinnersAnnouncementHeader } from '@components/atoms/WinnersAnnouncementHeader'
import { ILeaderboardListItem } from '@components/pages/Contest/Leaderboard/LeaderboardList'
import { Button } from '@/components/atoms/Button'
import { OriginChip } from '@/components/atoms/OriginChip'

import { IContestDiscoveryFeedContestData } from '@services/contestDiscoveryFeed'
import { thumbnailUrl } from '@utils/url'
import { formatDistanceToNowDate } from '@/utils/date'

export interface IContestWinnersAnnouncementFeedActivityProps {
  contestName: string
  winners: ILeaderboardListItem[]
  contestId: string
  data: IContestDiscoveryFeedContestData
  postMessage?: string
  time: string
  origin?: string
}

export const ContestWinnersAnnouncementFeedActivity: FC<
  IContestWinnersAnnouncementFeedActivityProps
> = ({ contestName, winners, contestId, data, postMessage, time, origin }) => {
  const { t } = useTranslation('common')
  const history = useHistory()
  const backgroundImages = useMemo(
    () =>
      winners.map(
        (winner) =>
          `url('${thumbnailUrl({
            userId: winner.userId,
            contestId,
          })}')`
      ),
    [winners, contestId]
  )

  return (
    <div className="bg-cool-gray-900 flex flex-col text-lg border-solid border-y-2 border-cool-gray-800 relative !z-0">
      <WinnersAnnouncementHeader>
        <Trans
          className="mr-1"
          i18nkey={`contest.winnersAnnouncement${
            postMessage ? 'Custom' : ''
          }Header`}
          values={{
            contestName,
            winnersAmount: winners.length,
          }}
        >
          {t(
            `contest.winnersAnnouncement${postMessage ? 'Custom' : ''}Header`,
            {
              contestName,
              winnersAmount: winners.length,
            }
          )}
        </Trans>
        <IonRouterLink className="mr-1" routerLink={`/contest/${contestId}`}>
          <strong>{contestName}</strong>
        </IonRouterLink>
        <OriginChip origin={origin} />
      </WinnersAnnouncementHeader>
      <div className="relative grow bg-cover bg-no-repeat bg-center flex flex-col">
        {winners.length > 1 ? (
          <Swiper
            modules={[Navigation, Pagination, Virtual]}
            direction="horizontal"
            className="swiper-feed w-full h-full"
            navigation
            pagination={{
              clickable: false,
            }}
            virtual
          >
            {winners.map((winner, index) => (
              <SwiperSlide
                key={winner.userId}
                className="relative grow bg-no-repeat bg-center flex flex-col"
                virtualIndex={index}
              >
                <IonRouterLink
                  routerLink={`/contest/${contestId}/video/${winner.userId}`}
                  className="w-full bg-cover h-full flex items-center min-h-490 cursor-pointer"
                  style={{
                    backgroundImage: backgroundImages[index],
                  }}
                ></IonRouterLink>
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <div className="relative grow bg-no-repeat bg-center flex flex-col">
            <IonRouterLink
              className="w-full bg-cover h-full flex items-center min-h-490 cursor-pointer"
              routerLink={`/contest/${contestId}/video/${winners[0]?.userId}`}
              style={{
                backgroundImage: backgroundImages[0],
              }}
            ></IonRouterLink>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 z-10">
          <Button
            className="m-3"
            data-e2e-feed-detail
            onClick={(e) => {
              e.stopPropagation()
              history.push(`/contest/${contestId}/leaderboard`)
            }}
          >
            {t('contest.viewLeaderboard')}
          </Button>
          <div className="bg-dark p-3 pb-6">
            <div className="text-sm font-normal mb-2">
              <IonRouterLink
                className="mr-1 float-left"
                routerLink={`/profile/${winners[0]?.userId}`}
              >
                <strong>{`@${winners[0]?.username}`}</strong>
              </IonRouterLink>
              <div className="text-cool-gray-200">
                <Trans
                  i18nkey={`contest.winnersAnnouncement${
                    postMessage ? 'Custom' : ''
                  }`}
                  values={{
                    username: winners[0]?.username,
                    seasonNumber: data?.seasonNumber,
                    contestName,
                    otherWinnersAmount: winners.length - 1,
                    postMessage,
                  }}
                  count={winners.length}
                >
                  {t(
                    `contest.winnersAnnouncement${postMessage ? 'Custom' : ''}`,
                    {
                      username: winners[0]?.username,
                      seasonNumber: data?.seasonNumber,
                      contestName,
                      otherWinnersAmount: winners.length - 1,
                      count: winners.length,
                      postMessage,
                    }
                  )}
                </Trans>
              </div>
            </div>
            <span className="block text-xs opacity-50">
              {formatDistanceToNowDate({
                timestamp: time,
                addSuffix: false,
                useUTC: false,
              })}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
